import { apiClient, v3ODataClient } from '@api/apiClient';
import { buildChangeTrackingRequestConfig } from '@changeTracking/utils/buildChangeTrackingHeaders';
import { CHANGE_TRACKING_SOURCES } from '@changeTracking/utils/changeTrackingRegistry';
import { ToastType } from '@components/Toast/Toast';
import { FeatureFlags } from '@constants/featureFlags';
import { isFeatureFlagEnabled } from '@store/featureFlags/store';
import { orderStore, setOrderStore } from '@store/orders/store';
import {
  CommitQuoteResult,
  FalveyInsuranceQuote,
  LtlAccessorialCatalogItem,
  LtlApiRequestDetailsViewModel,
  LtlApiRequestSummaryViewModel,
  LtlQuoteAccessorialViewModel,
  LtlQuoteLineItemViewModel,
  LtlQuoteLoadItemViewModel,
  LtlQuoteParametersViewModel,
  LtlQuoteViewModel,
  LtlTerminalInfoViewModel,
  ODataApiResponse,
  PickUpDropOffItem,
  QuoteParametersAccessorialViewModel,
  SubmitShipmentResult,
} from '@store/orders/types';
import { ILoadEditViewModel } from '@typeDefinitions/loadTypes';
import { logError } from '@utils/errorLogging';
import { createError } from '@utils/errorUtils';
import { handleToast } from '@utils/utils';

export async function fetchProductCatalog(queryParams: {
  $format?: string;
  $top?: number;
  $skip?: number;
  $orderby?: string;
  $count?: boolean;
  $filter?: string;
}) {
  try {
    const response: ODataApiResponse = await v3ODataClient.get('LTLCatalogs', {
      params: queryParams,
    });
    if ('value' in response) {
      return {
        data: response.value,
        count: response['@odata.count'],
      };
    }
    throw new Error('Failed to fetch product catalog');
  } catch (error: unknown) {
    logError('fetchProductCatalog', error as Error);
    throw createError(error, 'Failed to fetch product catalog');
  }
}

export async function saveToLTLCatalog(
  item: PickUpDropOffItem,
  customerId: number,
) {
  try {
    const response = await apiClient.post(
      '/LTLCatalog',
      {
        customerId: customerId,
        name: item.description,
        description: item.description,
        class: String(item.class),
        nmfc: item.nmfc,
        hazmat: item.hazmat,
        lengthInches: item.lengthInch,
        widthInches: item.widthInch,
        heightInches: item.heightInch,
        ltlCatalogItemHazmatDetail: item.loadItemHazmatDetail,
      },
      buildChangeTrackingRequestConfig([
        CHANGE_TRACKING_SOURCES.orderDetails.stopsSaveLtlCatalog,
      ]),
    );
    handleToast(ToastType.Success, 'Item saved to LTL Catalog');
    // NOTE: response is not used anywhere add a notification later
    return response;
  } catch (error) {
    logError('saveToLTLCatalog', error as Error);
    const err = createError(error, 'Failed to save to LTL Catalog');
    handleToast(ToastType.Error, err.message);
  }
}

export async function fetchLtlQuoteParametersByLoadId(
  loadId: number,
): Promise<LtlQuoteParametersViewModel> {
  try {
    const response = await apiClient.get<{
      value?: LtlQuoteParametersViewModel;
    }>(`/Ltl/GetQuoteParametersByLoadId/${loadId}`);

    if ('value' in response) {
      return response.value as LtlQuoteParametersViewModel;
    }
    throw new Error(
      'Invalid response format: cannot get value of Quote from response.',
    );
  } catch (error: unknown) {
    logError('GetQuoteParametersByLoadId', error as Error);
    throw new Error(
      `Failed to fetch LTL Quote Parameters for load ${loadId}: ${String(
        error,
      )}`,
    );
  }
}

export async function fetchLtlQuoteByLoadId(
  loadId: number,
): Promise<LtlQuoteViewModel | null> {
  try {
    const response = await apiClient.get<{
      value?: LtlQuoteViewModel;
    }>(`/Ltl/GetLtlQuoteByLoadId/${loadId}`);

    if ('value' in response) {
      return response.value as LtlQuoteViewModel;
    }
    throw new Error(
      'Invalid response format: cannot get value of Quote from response.',
    );
  } catch (error: unknown) {
    logError(
      'GetLtlQuoteByLoadId',
      error instanceof Error ? error : new Error(String(error)),
    );
    throw new Error(
      `Failed to fetch LTL Quote for loadId ${loadId}. ${
        error instanceof Error ? error.message : error
      }`,
    );
  }
}

export async function fetchLtlQuoteLineItems(
  quoteId: number,
): Promise<LtlQuoteLineItemViewModel[]> {
  try {
    const response = await apiClient.get<{
      value?: LtlQuoteLineItemViewModel[];
    }>(`/Ltl/GetQuoteLineItems/${quoteId}`);

    if ('value' in response) {
      return response.value as LtlQuoteLineItemViewModel[];
    }
    throw new Error(
      'Invalid response format: cannot get value of Line Items from response.',
    );
  } catch (error: unknown) {
    logError(
      'GetQuoteLineItems',
      error instanceof Error ? error : new Error(String(error)),
    );
    throw new Error(
      `Failed to fetch LTL Quote Line Items for quoteId ${quoteId}. ${
        error instanceof Error ? error.message : error
      }`,
    );
  }
}

export async function fetchLtlQuoteLoadItems(
  quoteParametersId: number,
): Promise<LtlQuoteLoadItemViewModel[]> {
  try {
    const response = await apiClient.get<{
      value?: LtlQuoteLoadItemViewModel[];
    }>(`/Ltl/GetQuoteLoadItems/${quoteParametersId}`);

    if ('value' in response) {
      return response.value as LtlQuoteLoadItemViewModel[];
    }
    throw new Error(
      'Invalid response format: cannot get value of Load Items from response.',
    );
  } catch (error: unknown) {
    logError(
      'GetQuoteLoadItems',
      error instanceof Error ? error : new Error(String(error)),
    );
    throw new Error(
      `Failed to fetch LTL Quote Load Items for quote parameters Id ${quoteParametersId}. ${
        error instanceof Error ? error.message : error
      }`,
    );
  }
}

// AccessorialType IDs are stable DB values: 1 = Pickup, 2 = Delivery, 3 = Shipment.
const ACCESSORIAL_TYPE_ID_PICKUP = 1;

const ACCESSORIAL_TYPE_ID_DELIVERY = 2;

function mapQuoteParamsAccessorialToViewModel(
  item: QuoteParametersAccessorialViewModel,
  index: number,
): LtlQuoteAccessorialViewModel {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
  const typeId = item.accessorial.accessorialType?.id;
  const isPickup = typeId === ACCESSORIAL_TYPE_ID_PICKUP;
  const isDelivery = typeId === ACCESSORIAL_TYPE_ID_DELIVERY;
  return {
    id: index,
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    accessorialId: String(item.accessorial.id),
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    quoteParametersId: item.quoteParametersId,
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    accessorialName: item.accessorial.displayName,
    accessorialDescription:
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      item.accessorial.accessorialType?.description ?? 'Specialized Services',
    isPickup,
    isDelivery,
    isGlobal: !isPickup && !isDelivery,
  };
}

export async function fetchLtlAccessorialCatalog(): Promise<
  LtlAccessorialCatalogItem[]
> {
  try {
    const response = await apiClient.get<{
      value?: LtlAccessorialCatalogItem[];
    }>('/ltl/Accessorials');

    if ('value' in response) {
      return response.value as LtlAccessorialCatalogItem[];
    }
    throw new Error(
      'Invalid response format: cannot get value of Accessorial catalog from response.',
    );
  } catch (error: unknown) {
    logError(
      'GetAccessorials',
      error instanceof Error ? error : new Error(String(error)),
    );
    throw new Error(
      `Failed to fetch LTL Accessorial catalog. ${
        error instanceof Error ? error.message : error
      }`,
    );
  }
}

export async function fetchQuoteParametersAccessorials(
  quoteParametersId: number,
): Promise<LtlQuoteAccessorialViewModel[]> {
  try {
    const response = await apiClient.get<{
      value?: QuoteParametersAccessorialViewModel[];
    }>(`/ltl/QuoteParametersAccessorials/${quoteParametersId}`);

    if ('value' in response) {
      const items =
        response.value as unknown as QuoteParametersAccessorialViewModel[];
      return items.map(mapQuoteParamsAccessorialToViewModel);
    }
    throw new Error(
      'Invalid response format: cannot get value of QuoteParametersAccessorials from response.',
    );
  } catch (error: unknown) {
    logError(
      'QuoteParametersAccessorials',
      error instanceof Error ? error : new Error(String(error)),
    );
    throw new Error(
      `Failed to fetch QuoteParametersAccessorials for quoteParametersId ${quoteParametersId}. ${
        error instanceof Error ? error.message : error
      }`,
    );
  }
}

export async function fetchLtlQuoteAccessorials(
  quoteParametersId: number,
): Promise<LtlQuoteAccessorialViewModel[]> {
  if (isFeatureFlagEnabled(FeatureFlags.LtlAccessorialsSchemaRefactorRead)()) {
    return fetchQuoteParametersAccessorials(quoteParametersId);
  }
  try {
    const response = await apiClient.get<{
      value?: LtlQuoteAccessorialViewModel[];
    }>(`/Ltl/GetQuoteAccessorials/${quoteParametersId}`);

    if ('value' in response) {
      return response.value as LtlQuoteAccessorialViewModel[];
    }
    throw new Error(
      'Invalid response format: cannot get value of Accessorials from response.',
    );
  } catch (error: unknown) {
    logError(
      'GetQuoteAccessorials',
      error instanceof Error ? error : new Error(String(error)),
    );
    throw new Error(
      `Failed to fetch LTL Quote Accessorials for quote parameters Id ${quoteParametersId}. ${
        error instanceof Error ? error.message : error
      }`,
    );
  }
}

export async function fetchLtlQuoteInsurance(
  quoteParametersId: number,
): Promise<FalveyInsuranceQuote> {
  try {
    const response = await apiClient.get<{
      value?: FalveyInsuranceQuote;
    }>(`/Ltl/GetInsuranceQuote/${quoteParametersId}`);

    if ('value' in response) {
      return response.value as FalveyInsuranceQuote;
    }
    throw new Error(
      'Invalid response format: cannot get value of Insurance from response.',
    );
  } catch (error: unknown) {
    logError(
      'GetInsuranceQuote',
      error instanceof Error ? error : new Error(String(error)),
    );
    throw new Error(
      `Failed to fetch LTL Insurance Quote for quote parameters Id ${quoteParametersId}. ${
        error instanceof Error ? error.message : error
      }`,
    );
  }
}

export async function fetchLtlQuoteTerminals(
  quoteId: string,
): Promise<LtlTerminalInfoViewModel> {
  try {
    const response = await apiClient.get<{
      value?: LtlTerminalInfoViewModel;
    }>(`/Ltl/GetQuoteTerminals/${quoteId}`);

    if ('value' in response) {
      return response.value as LtlTerminalInfoViewModel;
    }
    throw new Error(
      'Invalid response format: cannot get value of Terminals from response.',
    );
  } catch (error: unknown) {
    logError(
      'GetQuoteTerminals',
      error instanceof Error ? error : new Error(String(error)),
    );
    throw new Error(
      `Failed to fetch LTL Terminal Info for quote Id ${quoteId}. ${
        error instanceof Error ? error.message : error
      }`,
    );
  }
}

export async function fetchLtlApiRequestSummary(
  quoteId: string,
): Promise<LtlApiRequestSummaryViewModel[]> {
  try {
    const response = await apiClient.get<{
      value?: LtlApiRequestSummaryViewModel[];
    }>(`/Ltl/ApiRequestSummary/${quoteId}`);

    if ('value' in response) {
      return response.value as LtlApiRequestSummaryViewModel[];
    }
    throw new Error(
      'Invalid response format: cannot get value of API Resquest Summary from response.',
    );
  } catch (error: unknown) {
    logError(
      'fetchLtlApiRequestSummary',
      error instanceof Error ? error : new Error(String(error)),
    );
    throw new Error(
      `Failed to fetch API Resquest Summary for quote Id ${quoteId}. ${
        error instanceof Error ? error.message : error
      }`,
    );
  }
}

export async function fetchLtlApiRequestDetail(
  quoteId: number,
): Promise<LtlApiRequestDetailsViewModel> {
  try {
    const response = await apiClient.get<{
      value?: LtlApiRequestDetailsViewModel;
    }>(`/Ltl/ApiRequestDetails/${quoteId}`);

    if ('value' in response) {
      return response.value as LtlApiRequestDetailsViewModel;
    }
    throw new Error(
      'Invalid response format: cannot get value of API Resquest Details from response.',
    );
  } catch (error: unknown) {
    logError(
      'fetchLtlApiRequestDetails',
      error instanceof Error ? error : new Error(String(error)),
    );
    throw new Error(
      `Failed to fetch API Resquest Details for quote Id ${quoteId}. ${
        error instanceof Error ? error.message : error
      }`,
    );
  }
}

type LtlDispatchResponse = {
  submitShipmentResult: SubmitShipmentResult[] | SubmitShipmentResult;
  commitQuoteResult: CommitQuoteResult;
  message?: string;
};

function handleLtlDispatchResponse(response: unknown) {
  const result = response as LtlDispatchResponse;
  const submitShipmentResult = Array.isArray(result.submitShipmentResult)
    ? result.submitShipmentResult[0]
    : result.submitShipmentResult;
  const submitShipmentMessage = submitShipmentResult.message;

  const index = orderStore.activeTab.index;
  const dispatchSucceeded = submitShipmentResult.code === 'Success';
  const insuranceCommitSucceededOrIsNotApplicable =
    result.commitQuoteResult.errors?.length === 0;
  const insuranceCommitApplied =
    result.commitQuoteResult.certificatePDFLink !== null;
  const dispatchErrors = submitShipmentMessage;
  const insuranceCommitErrors = result.commitQuoteResult.errors?.join(', ');

  if (
    dispatchSucceeded &&
    insuranceCommitSucceededOrIsNotApplicable &&
    insuranceCommitApplied
  ) {
    setOrderStore('order', 'loads', index, 'status', 'Dispatched');
    handleToast(
      ToastType.Success,
      result.message ??
        'Pickup request submitted and Insurance successfully committed.',
    );
    return result;
  } else if (
    dispatchSucceeded &&
    insuranceCommitSucceededOrIsNotApplicable &&
    !insuranceCommitApplied
  ) {
    setOrderStore('order', 'loads', index, 'status', 'Dispatched');
    handleToast(
      ToastType.Success,
      result.message ?? 'Pickup request submitted.',
    );
    return result;
  } else if (dispatchSucceeded && !insuranceCommitSucceededOrIsNotApplicable) {
    setOrderStore('order', 'loads', index, 'status', 'Dispatched');
    handleToast(
      ToastType.Success,
      (result.message ?? 'Pickup request successfully submitted') +
        ', but the insurance commit failed. Error(s): ' +
        insuranceCommitErrors,
    );
  } else if (
    !dispatchSucceeded &&
    insuranceCommitSucceededOrIsNotApplicable &&
    insuranceCommitApplied
  ) {
    handleToast(
      ToastType.Error,
      'Error submitting pickup, but the insurance commit succeeded. Error(s): ' +
        dispatchErrors,
    );
  } else if (!dispatchSucceeded && !insuranceCommitSucceededOrIsNotApplicable) {
    handleToast(
      ToastType.Error,
      'Error submitting pickup. Error(s): ' +
        dispatchErrors +
        ', Insurance commit failed. Error(s): ' +
        insuranceCommitErrors,
    );
  } else {
    handleToast(
      ToastType.Error,
      'Error submit pickup. Error(s):' + dispatchErrors,
    );
  }

  return result;
}

export async function dispatchLtlLoad(loadId: number) {
  try {
    const response = await apiClient.post(
      `Ltl/tms/dispatch/${loadId}`,
      undefined,
      buildChangeTrackingRequestConfig([
        CHANGE_TRACKING_SOURCES.orderDetails.loadDetailsSubmitPickupRequest,
      ]),
    );
    return handleLtlDispatchResponse(response);
  } catch (error: unknown) {
    logError('dispatchLtlLoad', error as Error);
    throw createError(error, 'Failed to dispatch LTL load');
  }
}

export async function requestPickupLegacy(payload: ILoadEditViewModel) {
  try {
    const response = await apiClient.post(
      'ltl/submitshipment',
      payload,
      buildChangeTrackingRequestConfig([
        CHANGE_TRACKING_SOURCES.orderDetails.loadDetailsRequestPickup,
      ]),
    );
    return handleLtlDispatchResponse(response);
  } catch (error: unknown) {
    logError('requestPickupLegacy', error as Error);
    throw createError(error, 'Failed to request pickup');
  }
}
